import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowLeft, ShieldOff } from 'lucide-react';
import { useProjectName } from '@/app/layout/ProjectNameContext';

/**
 * Wrapper component at the /project/:appId route level.
 * Loads project config + data sources once, validates mandatory fields,
 * and provides data via ProjectNameContext to all child routes
 * (project detail page + project settings pages).
 */
export default function ProjectWrapper() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const projectDetails = (location.state as any)?.projectDetails;
  const { projectConfig, configLoading, dataSources: ctxDataSources, setProject, clearProject, loadProject } = useProjectName();

  const [accessDenied, setAccessDenied] = useState(false);

  // Load project data and validate mandatory fields
  useEffect(() => {
    if (!appId) return;
    const load = async () => {
      try {
        const { config, dataSources } = await loadProject(appId);

        // Set project name from config or router state
        const name = config.appName || projectDetails?.appName;
        if (name) setProject(appId, name);

        // Check mandatory fields — redirect to data sources if no git repos
        const isSettingsPage = location.pathname.includes('/settings');
        if (!isSettingsPage) {
          const hasGitRepos = dataSources.some((ds: any) => ds.platform === 'Git');
          if (!hasGitRepos) {
            navigate(`/project/${appId}/settings/data-sources`, { replace: true });
          }
        }
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setAccessDenied(true);
          return;
        }
        if (projectDetails?.appName) {
          setProject(appId, projectDetails.appName);
        }
      }
    };
    load();
  }, [appId]);

  // Enforce git repo requirement on every navigation
  // If user removed all repos and navigates back to project page, redirect to data-sources
  useEffect(() => {
    if (!appId || configLoading) return;
    const isSettingsPage = location.pathname.includes('/settings');
    if (!isSettingsPage && ctxDataSources.length >= 0) {
      const hasGitRepos = ctxDataSources.some((ds: any) => ds.platform === 'Git');
      if (!hasGitRepos && projectConfig) {
        navigate(`/project/${appId}/settings/data-sources`, { replace: true });
      }
    }
  }, [appId, location.pathname, ctxDataSources]);

  // Clear project on unmount (leaving project scope)
  useEffect(() => {
    return () => clearProject();
  }, []);

  if (accessDenied) {
    return (
      <Stack sx={{ height: '100%', overflow: 'hidden' }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
            }}
          >
            <ShieldOff size={32} color={theme.palette.error.main} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
            You don't have access to this project. Ask a Project Admin to add you as a team member, or request access to the project's organization unit.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/projects')}
            sx={{ mt: 1 }}
          >
            Back to Projects
          </Button>
        </Box>
      </Stack>
    );
  }

  // Show spinner until project config is loaded
  if (configLoading && !projectConfig) {
    return (
      <Stack sx={{ height: '100%', overflow: 'hidden' }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
          <CircularProgress size={28} thickness={4} />
          <Typography variant="body2" color="text.secondary" mt={1.5}>Loading project details...</Typography>
        </Box>
      </Stack>
    );
  }

  return <Outlet />;
}
